import { useMemo, useEffect, useRef, useState } from 'react';
import {
  AddNeedResourceDetailsToActionRequestDto,
  NeedDto,
  NeedsActionDto,
  NeedsActivityDto,
  SuggestedActionDto,
} from '@act/shared/data-transfer-objects';
import {
  Box,
  IconButton,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { ArrowLeftIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionFormValues,
  NeedActionFormSchema,
} from '@act/react-components/dialog-forms/components/useNeedActionFormtypes';
import { useForm, useFieldArray } from 'react-hook-form';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { FormBuilder, HtmlDisplayer, ReemaThemeV2 } from '@act/storybook';
import { metaSliceSelectors, suggestedActionUpdated } from 'redux/redux';
import { DateTime } from 'luxon';
import { DATE_FORMAT } from '@act/react-components/dialog-forms/scheduled-event-dialog';
import { useApiCall } from 'redux/utils/api-hook';
import { needRequests } from 'redux/requests/need.requests';
import { GoalsSidePanel } from './GoalsSidePanel';
import { useToast } from '@act/react-components/app/ReusableToast';
import useUnsavedChangesDialog from '@act/react-components/dialog-forms/hooks/use-unsaved-changes-dialog';
import { generateDatadogTrackingProps } from '@act/toolkit/environment/environment/user-monitoring';
import syvaNeedsUtils from '../syva-needs-utils';
import { useAppDispatch } from 'redux/store-hooks';
import { convertTimeStringToMinutes } from '@act/react-components/dialog-forms/helpers/timeConverter';
import { setSidePanelIsDirty } from '@act/react-components/member-page/Calls/FullScreenSaveClose.utils';

interface NeedsDetailAddActionProps {
  open: boolean;
  onClose: () => void;
  need: NeedDto;
  action?: NeedsActionDto;
  suggestedAction?: SuggestedActionDto;
}
type ResourceDetailsRequestWithId = AddNeedResourceDetailsToActionRequestDto & {
  id: number;
};
export const NeedsDetailAddAction = ({
  open,
  onClose,
  need,
  action,
  suggestedAction,
}: NeedsDetailAddActionProps) => {
  const dispatch = useAppDispatch();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const [durationFocused, setDurationFocused] = useState(false);
  const isEditing = Boolean(action);
  const needMetadata = metaSliceSelectors.useSelectNeedsMetadata();
  const { activities, issues } =
    metaSliceSelectors.useSelectNeedsMetadata().data;
  const issue = useMemo(
    () => issues.find(i => i.id === need?.issueId),
    [need?.issueId, issues]
  );
  const category = needMetadata.data.categories.find(
    c => c.id === issue?.categoryId
  );
  const daysRemaining = syvaNeedsUtils.calculateRemainingDays(
    need?.expiresAt || 0
  );
  const toast = useToast();
  const defaultDate = DateTime.now().toFormat(DATE_FORMAT);
  const referralsCountRef = useRef<number>(null);

  function formatResourceReferrals(
    resourceRequests: ResourceDetailsRequestWithId[]
  ): AddNeedResourceDetailsToActionRequestDto[] {
    return resourceRequests.map(details => {
      const { id, phoneNumber, ...data } = details;
      const formattedPhoneNumber = phoneNumber
        ? '+1'.concat(phoneNumber?.replace(/\D/g, ''))
        : undefined;
      return {
        ...data,
        ...(formattedPhoneNumber && { phoneNumber: formattedPhoneNumber }),
        ...(id > 0 && { id: details.id }),
      };
    });
  }
  const activityOptions: NeedsActivityDto[] = useMemo(
    () =>
      activities
        .filter(a => {
          return issue.activityIds.includes(a.id);
        })
        .sort((a, b) => {
          if (b.label < a.label) return 1;
          if (b.label > a.label) return -1;
          return 0;
        }),
    [activities, issue]
  );

  const { control, handleSubmit, formState, watch, reset, getValues } =
    useForm<ActionFormValues>({
      defaultValues: {
        activityId: action?.activityId || suggestedAction?.activityId || 0,
        date: action?.date || defaultDate,
        duration: action?.durationInMinutes?.toString() ?? '',
        description: action?.description || suggestedAction?.description || '',
        resourceReferred: Boolean(action?.resourceReferrals?.length),
        markAsFulfilled: false,
        resourceDetails:
          action?.resourceReferrals?.map(resource => ({
            id: resource.id,
            resourceName: resource.resourceName,
            contactName: resource.contactName || '',
            phoneNumber: resource.phoneNumber || '',
            url: resource.url || '',
            streetAddress: resource.streetAddress || '',
            city: resource.city || '',
            state: resource.state || '',
            zipcode: resource.zipcode || '',
            shouldDelete: false,
          })) || [],
      },
      resolver: zodResolver(NeedActionFormSchema),
    });

  const {
    fields: resourceDetailsFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'resourceDetails',
    keyName: 'key',
  });

  const resourceReferred = watch('resourceReferred');

  const [createdAction, createActionLoading, createAction] = useApiCall(
    needRequests.addActionToNeed
  );

  useEffect(() => {
    if (action) {
      const allActions = [action, ...(action.subsequentActions || [])];
      allActions.sort((a, b) => b.createdAt - a.createdAt);
      const latestAction = allActions[0];

      const allResourceReferrals = [];

      if (action.resourceReferrals) {
        allResourceReferrals.push(...action.resourceReferrals);
      }

      // Add subsequent action referrals
      if (latestAction?.resourceReferrals && latestAction !== action) {
        allResourceReferrals.push(...latestAction.resourceReferrals);
      }

      reset({
        activityId: latestAction?.activityId,
        date: `${latestAction?.date}`,
        duration: latestAction?.durationInMinutes?.toString() ?? '',
        description: latestAction.description,
        resourceReferred: allResourceReferrals.length > 0,
        resourceDetails: allResourceReferrals.map(resource => ({
          id: resource.id,
          resourceName: resource.resourceName,
          contactName: resource.contactName || '',
          phoneNumber: resource.phoneNumber || '',
          url: resource.url || '',
          streetAddress: resource.streetAddress || '',
          city: resource.city || '',
          state: resource.state || '',
          zipcode: resource.zipcode || '',
          shouldDelete: false,
        })),
      });
    } else if (suggestedAction) {
      reset({
        activityId: suggestedAction.activityId,
        date: defaultDate,
        duration: '',
        description: suggestedAction.description,
        resourceReferred: false,
        markAsFulfilled: false,
        resourceDetails: [],
      });
    } else {
      reset({
        activityId: 0,
        date: defaultDate,
        duration: '',
        description: '',
        resourceReferred: false,
        markAsFulfilled: false,
        resourceDetails: [],
      });
    }
  }, [action, suggestedAction, reset, defaultDate]);

  const addResourceDetail = () => {
    append({
      id: undefined,
      resourceName: '',
      contactName: '',
      phoneNumber: '',
      url: '',
      streetAddress: '',
      city: '',
      state: '',
      zipcode: '',
      shouldDelete: false,
    });
  };

  useEffect(() => {
    if (referralsCountRef.current > resourceDetailsFields.length) {
      referralsCountRef.current = referralsCountRef.current - 1;
    }
  }, [resourceDetailsFields.length]);

  useEffect(() => {
    if (resourceDetailsFields.length >= referralsCountRef.current) {
      referralsCountRef.current = resourceDetailsFields.length;
      const key = resourceDetailsFields[resourceDetailsFields.length - 1]?.key;
    }
  }, [resourceDetailsFields]);

  const removeResourceDetail = (index: number) => {
    remove(index);
  };

  useEffect(() => {
    if (createdAction && !!createdAction.originalActionId) {
      toast('Action updated successfully');
      reset();
      onClose();
    }
    if (createdAction && !createdAction.originalActionId) {
      if (suggestedAction) {
        dispatch(suggestedActionUpdated(suggestedAction));
      }
      toast('Action created successfully');
      reset();
      onClose();
    }
  }, [createdAction]);

  const { isDirty } = formState;

  useEffect(() => {
    if (open) {
      setSidePanelIsDirty(isDirty);
    }
  }, [isDirty, open]);

  useEffect(() => {
    if (!open) {
      setSidePanelIsDirty(false);
    }
    return () => {
      setSidePanelIsDirty(false);
    };
  }, [open]);

  const onSubmit = (data: ActionFormValues) => {
    const resources =
      data?.resourceDetails?.length > 0
        ? formatResourceReferrals(
            data?.resourceDetails as ResourceDetailsRequestWithId[]
          )
        : null;
    createAction({
      params: { needId: need.id },
      body: {
        activityId: data.activityId,
        date: data.date,
        durationInMinutes: Number(convertTimeStringToMinutes(data.duration)),
        description: data.description,
        ...(!!suggestedAction &&
          !!suggestedAction.tempNeedId && {
            tempNeedId: suggestedAction.tempNeedId,
          }),
        ...(!!action && {
          originalActionId: action?.originalActionId
            ? action.originalActionId
            : action.id,
        }),
        ...(resources && {
          resourceReferrals: resources,
        }),
        markAsFulfilled: data.markAsFulfilled,
        ...(!!suggestedAction &&
          !!suggestedAction.id && {
            suggestedActionId: suggestedAction.id,
          }),
      },
    });
  };

  const activitySelectOptions = useMemo(() => {
    return activityOptions.map(activity => ({
      label: activity.label,
      value: activity.id,
    }));
  }, [activityOptions]);

  const formConfig: FormConfig<ActionFormValues> = [
    {
      type: 'input',
      name: 'activityId',
      label: 'Activity',
      input: 'select',
      props: {
        options: activitySelectOptions,
        fullWidth: true,
      },
    },
    {
      type: 'input',
      name: 'date',
      label: 'Date',
      input: 'date',
      props: {
        defaultValue: defaultDate,
        inputType: 'date',
      },
    },
    {
      type: 'input',
      name: 'duration',
      label: 'Duration',
      input: 'text',
      props: {
        required: true,
        iconAdornment:
          !durationFocused && getValues('duration') !== '' ? <>minutes</> : '',
        onFocusCallback: () => {
          setDurationFocused(true);
        },
        onBlurCallback: (e, fn) => {
          const value = e.target.value;
          setDurationFocused(false);
          fn(value);
        },
      },
    },
    {
      type: 'input',
      name: 'description',
      label: 'Description',
      input: 'text',
      props: {
        required: true,
        multiline: true,
        minRows: 4,
      },
    },
    {
      type: 'group',
      items: [
        {
          type: 'input',
          name: 'markAsFulfilled',
          label: 'Mark as Fulfilled',
          input: 'checkbox',
        },
        {
          type: 'input',
          name: 'resourceReferred',
          label: 'Resource Referred',
          input: 'checkbox',
        },
      ],
    },
  ];

  const handleCancel = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        reset();
        onClose();
      });
    } else {
      reset();
      onClose();
    }
  };

  const content = useMemo(
    () => (
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: '16px',
            mb: 2,
          }}
        >
          <IconButton onClick={handleCancel}>
            <ArrowLeftIcon size={32} />
          </IconButton>
          <Typography sx={{ alignSelf: 'center' }} variant="titleLarge">
            {isEditing ? 'Edit Action' : 'Add Action'}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: ReemaThemeV2.LIGHT_MODE.palette.navyShades[100],
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '6px',
              alignItems: 'center',
            }}
          >
            <Typography variant="subheadMedium" fontWeight={'bold'}>
              Need:{' '}
            </Typography>
            <Typography variant="bodyMedium" fontWeight={'regular'}>
              {issue?.label}
            </Typography>
          </Box>
          <Box>
            <Typography>
              <HtmlDisplayer innerHtml={need.reason} />
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '49px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography variant="subheadMedium" fontWeight="bold">
                Category
              </Typography>
              <Typography variant="bodyMedium">{category?.label}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography variant="subheadMedium" fontWeight="bold">
                Remaining time
              </Typography>
              <Typography variant="bodyMedium">
                {`${daysRemaining} days remaining`}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ padding: '16px 0 16px' }}>
          <FormBuilder
            config={formConfig}
            control={control}
            formState={formState}
          />

          {resourceReferred && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subheadMedium"
                fontWeight="bold"
                sx={{ mb: 2 }}
              >
                Resource Details
              </Typography>
              {resourceDetailsFields.map((field, index) => (
                <Box
                  key={field.key}
                  sx={{
                    mb: 3,
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant="bodyMedium" fontWeight="medium">
                      Resource {index + 1}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => removeResourceDetail(index)}
                      startIcon={<XIcon size={16} />}
                      disabled={createActionLoading}
                    >
                      Remove
                    </Button>
                  </Box>

                  <FormBuilder
                    config={[
                      {
                        type: 'input',
                        name: `resourceDetails.${index}.resourceName`,
                        label: 'Resource Name',
                        input: 'text',
                        props: {
                          required: true,
                        },
                      },
                      {
                        type: 'group',
                        items: [
                          {
                            type: 'input',
                            name: `resourceDetails.${index}.contactName`,
                            label: 'Contact Name',
                            input: 'text',
                            props: {},
                          },
                          {
                            type: 'input',
                            name: `resourceDetails.${index}.phoneNumber`,
                            label: 'Phone Number',
                            input: 'phone',
                            props: {},
                          },
                        ],
                      },
                      {
                        type: 'input',
                        name: `resourceDetails.${index}.url`,
                        label: 'Website URL',
                        input: 'text',
                        props: {},
                      },
                      {
                        type: 'group',
                        items: [
                          {
                            type: 'input',
                            name: `resourceDetails.${index}.streetAddress`,
                            label: 'Street Address',
                            input: 'text',
                            props: {},
                          },
                          {
                            type: 'input',
                            name: `resourceDetails.${index}.city`,
                            label: 'City',
                            input: 'text',
                            props: {},
                          },
                        ],
                      },
                      {
                        type: 'group',
                        items: [
                          {
                            type: 'input',
                            name: `resourceDetails.${index}.state`,
                            label: 'State',
                            input: 'text',
                            props: {},
                          },
                          {
                            type: 'input',
                            name: `resourceDetails.${index}.zipcode`,
                            label: 'Zip Code',
                            input: 'text',
                            props: {},
                          },
                        ],
                      },
                    ]}
                    control={control}
                    formState={formState}
                    disabled={createActionLoading}
                  />
                </Box>
              ))}

              <Button
                variant="outlined"
                onClick={addResourceDetail}
                startIcon={<PlusIcon size={16} />}
                disabled={createActionLoading}
                sx={{ mt: 2 }}
              >
                Add Resource
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    ),
    [
      handleCancel,
      isEditing,
      issue?.label,
      need.reason,
      category?.label,
      daysRemaining,
      formConfig,
      resourceReferred,
      resourceDetailsFields,
      control,
      formState,
      createActionLoading,
      removeResourceDetail,
      addResourceDetail,
    ]
  );

  const footerContent = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2,
        width: '100%',
      }}
    >
      <Button
        variant="outlined"
        onClick={handleCancel}
        disabled={createActionLoading}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={handleSubmit(onSubmit)}
        disabled={createActionLoading}
        startIcon={createActionLoading ? <CircularProgress size={20} /> : null}
        {...generateDatadogTrackingProps(
          isEditing
            ? 'needs-update-action-from-call-submit-button'
            : 'needs-add-action-from-call-submit-button'
        )}
      >
        {createActionLoading
          ? isEditing
            ? 'Updating...'
            : 'Creating...'
          : isEditing
          ? 'Update Action'
          : 'Create Action'}
      </Button>
    </Box>
  );

  return (
    <GoalsSidePanel
      open={open}
      animated={false}
      titleContent={<></>}
      content={content}
      footerContent={footerContent}
    />
  );
};
